import { create } from 'zustand';
import type { Tenant, BitrixConnection, RegisteredBot, Flow, DashboardStats } from '../types';
import { api } from '../lib/api';

interface AppState {
  // Auth & Tenant
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;

  // Connections
  connections: BitrixConnection[];
  selectedConnection: BitrixConnection | null;

  // Bots
  bots: RegisteredBot[];
  selectedBot: RegisteredBot | null;

  // Flows
  flows: Flow[];
  selectedFlow: Flow | null;

  // Dashboard
  stats: DashboardStats | null;

  // Actions
  setTenant: (tenant: Tenant | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Data fetching
  fetchTenant: (tenantId: string) => Promise<void>;
  fetchConnections: (tenantId: string) => Promise<void>;
  fetchBots: (tenantId: string) => Promise<void>;
  fetchFlows: (tenantId: string) => Promise<void>;
  fetchStats: (tenantId: string) => Promise<void>;
  fetchAll: (tenantId: string) => Promise<void>;

  // Connection actions
  selectConnection: (connection: BitrixConnection | null) => void;
  reconnectConnection: (id: string) => Promise<void>;
  deleteConnection: (id: string) => Promise<void>;

  // Bot actions
  selectBot: (bot: RegisteredBot | null) => void;
  createBot: (connectionId: string, data: { name: string; code: string; description?: string; openLineIds?: number[] }) => Promise<RegisteredBot>;
  updateBot: (id: string, updates: Partial<RegisteredBot>) => Promise<void>;
  deleteBot: (id: string) => Promise<void>;
  assignFlowToBot: (botId: string, flowId: string) => Promise<void>;
  unassignFlowFromBot: (botId: string) => Promise<void>;
  toggleBot: (id: string, active: boolean) => Promise<void>;

  // Flow actions
  selectFlow: (flow: Flow | null) => void;
  createFlow: (data: { name: string; description?: string }) => Promise<Flow>;
  updateFlow: (id: string, updates: Partial<Flow>) => Promise<void>;
  publishFlow: (id: string) => Promise<void>;
  archiveFlow: (id: string) => Promise<void>;
  duplicateFlow: (id: string, name?: string) => Promise<Flow>;
  deleteFlow: (id: string) => Promise<void>;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  tenant: null,
  isLoading: false,
  error: null,
  connections: [],
  selectedConnection: null,
  bots: [],
  selectedBot: null,
  flows: [],
  selectedFlow: null,
  stats: null,

  // Basic setters
  setTenant: (tenant) => set({ tenant }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  // Data fetching
  fetchTenant: async (tenantId) => {
    try {
      set({ isLoading: true, error: null });
      const tenant = await api.getTenant(tenantId);
      set({ tenant, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchConnections: async (tenantId) => {
    try {
      const connections = await api.getConnections(tenantId);
      set({ connections });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  fetchBots: async (tenantId) => {
    try {
      const bots = await api.getBots(tenantId);
      set({ bots });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  fetchFlows: async (tenantId) => {
    try {
      const flows = await api.getFlows(tenantId);
      set({ flows });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  fetchStats: async (tenantId) => {
    try {
      const stats = await api.getDashboardStats(tenantId);
      set({ stats });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  fetchAll: async (tenantId) => {
    set({ isLoading: true, error: null });
    try {
      await Promise.all([
        get().fetchTenant(tenantId),
        get().fetchConnections(tenantId),
        get().fetchBots(tenantId),
        get().fetchFlows(tenantId),
        get().fetchStats(tenantId),
      ]);
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  // Connection actions
  selectConnection: (connection) => set({ selectedConnection: connection }),

  reconnectConnection: async (id) => {
    try {
      await api.reconnectConnection(id);
      const { tenant } = get();
      if (tenant) await get().fetchConnections(tenant.id);
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  deleteConnection: async (id) => {
    try {
      await api.deleteConnection(id);
      set((state) => ({
        connections: state.connections.filter((c) => c.id !== id),
        selectedConnection: state.selectedConnection?.id === id ? null : state.selectedConnection,
      }));
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  // Bot actions
  selectBot: (bot) => set({ selectedBot: bot }),

  createBot: async (connectionId, data) => {
    const bot = await api.createBot(connectionId, data);
    set((state) => ({ bots: [...state.bots, bot] }));
    return bot;
  },

  updateBot: async (id, updates) => {
    const updatedBot = await api.updateBot(id, updates);
    set((state) => ({
      bots: state.bots.map((b) => (b.id === id ? updatedBot : b)),
      selectedBot: state.selectedBot?.id === id ? updatedBot : state.selectedBot,
    }));
  },

  deleteBot: async (id) => {
    await api.deleteBot(id);
    set((state) => ({
      bots: state.bots.filter((b) => b.id !== id),
      selectedBot: state.selectedBot?.id === id ? null : state.selectedBot,
    }));
  },

  assignFlowToBot: async (botId, flowId) => {
    const updatedBot = await api.assignFlowToBot(botId, flowId);
    set((state) => ({
      bots: state.bots.map((b) => (b.id === botId ? updatedBot : b)),
      selectedBot: state.selectedBot?.id === botId ? updatedBot : state.selectedBot,
    }));
  },

  unassignFlowFromBot: async (botId) => {
    const updatedBot = await api.unassignFlowFromBot(botId);
    set((state) => ({
      bots: state.bots.map((b) => (b.id === botId ? updatedBot : b)),
      selectedBot: state.selectedBot?.id === botId ? updatedBot : state.selectedBot,
    }));
  },

  toggleBot: async (id, active) => {
    const updatedBot = await api.toggleBot(id, active);
    set((state) => ({
      bots: state.bots.map((b) => (b.id === id ? updatedBot : b)),
      selectedBot: state.selectedBot?.id === id ? updatedBot : state.selectedBot,
    }));
  },

  // Flow actions
  selectFlow: (flow) => set({ selectedFlow: flow }),

  createFlow: async (data) => {
    const { tenant, connections } = get();
    if (!tenant) throw new Error('No tenant');
    
    // Automatically assign the first connection if available
    const connectionId = connections.length > 0 ? connections[0].id : undefined;
    
    const flow = await api.createFlow(tenant.id, { ...data, connectionId });
    set((state) => ({ flows: [...state.flows, flow] }));
    return flow;
  },

  updateFlow: async (id, updates) => {
    const updatedFlow = await api.updateFlow(id, updates);
    set((state) => ({
      flows: state.flows.map((f) => (f.id === id ? updatedFlow : f)),
      selectedFlow: state.selectedFlow?.id === id ? updatedFlow : state.selectedFlow,
    }));
  },

  publishFlow: async (id) => {
    const updatedFlow = await api.publishFlow(id);
    set((state) => ({
      flows: state.flows.map((f) => (f.id === id ? updatedFlow : f)),
      selectedFlow: state.selectedFlow?.id === id ? updatedFlow : state.selectedFlow,
    }));
  },

  archiveFlow: async (id) => {
    const updatedFlow = await api.archiveFlow(id);
    set((state) => ({
      flows: state.flows.map((f) => (f.id === id ? updatedFlow : f)),
      selectedFlow: state.selectedFlow?.id === id ? updatedFlow : state.selectedFlow,
    }));
  },

  duplicateFlow: async (id, name) => {
    const newFlow = await api.duplicateFlow(id, name);
    set((state) => ({ flows: [...state.flows, newFlow] }));
    return newFlow;
  },

  deleteFlow: async (id) => {
    // Optimistic update: remove from UI immediately
    const previousFlows = get().flows;
    set((state) => ({
      flows: state.flows.filter((f) => f.id !== id),
      selectedFlow: state.selectedFlow?.id === id ? null : state.selectedFlow,
    }));

    try {
      await api.deleteFlow(id);
    } catch (error: any) {
      // Revert if failed
      set({ flows: previousFlows, error: error.message });
    }
  },
}));
